<template>
  <Transition name="slide-down">
    <div
      v-if="isImpersonating"
      class="fixed top-0 left-0 right-0 h-10 z-[60] flex items-center justify-center gap-4 px-4 bg-accent text-surface"
    >
      <div class="flex items-center gap-2">
        <FontAwesome :icon="['fas', 'user-secret']" class="w-4 h-4" />
        <span class="text-sm font-semibold">
          Impersonating: {{ impersonatedUsername }}
        </span>
        <span v-if="impersonatorUsername" class="text-sm opacity-75 hidden sm:inline">
          (as {{ impersonatorUsername }})
        </span>
      </div>

      <button
        @click="handleStopImpersonation"
        :disabled="isLoading"
        class="flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors bg-surface text-white hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesome
          :icon="['fas', isLoading ? 'spinner' : 'arrow-right-from-bracket']"
          :class="['w-4 h-4', isLoading ? 'animate-spin' : '']"
        />
        <span class="hidden sm:inline">{{ isLoading ? 'Returning...' : 'Return to My Account' }}</span>
        <span class="sm:hidden">{{ isLoading ? '...' : 'Exit' }}</span>
      </button>

      <span class="text-xs opacity-75 hidden md:inline">
        Session expires in {{ timeRemaining }}
      </span>
    </div>
  </Transition>
</template>

<script setup>
import { useImpersonation } from '@/composables/useImpersonation';

const {
  isImpersonating,
  impersonatorUsername,
  impersonatedUsername,
  isLoading,
  stopImpersonation,
} = useImpersonation();

// Track session time remaining (60 min expiry)
const timeRemaining = ref('60:00');
let timerInterval = null;
let sessionStartTime = null;

const handleStopImpersonation = async () => {
  const result = await stopImpersonation();
  if (!result.success) {
    console.error('Failed to stop impersonation:', result.error);
  }
};

const startTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }

  sessionStartTime = Date.now();
  const sessionDuration = 60 * 60 * 1000; // 60 minutes in ms

  timerInterval = setInterval(() => {
    const elapsed = Date.now() - sessionStartTime;
    const remaining = Math.max(0, sessionDuration - elapsed);

    if (remaining <= 0) {
      clearInterval(timerInterval);
      timeRemaining.value = 'Expired';
    } else {
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      timeRemaining.value = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }, 1000);
};

watch(isImpersonating, (val) => {
  if (val) {
    startTimer();
  } else if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
    timeRemaining.value = '60:00';
  }
}, { immediate: true });

onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
